import {
  type WorkflowStepField,
  WorkflowStepFieldKind,
  type WorkflowStepType,
} from 'src/gql/graphql';
import { WorkflowTriggerType } from 'src/graphql/entities/workflow/workflow.trigger';
import {
  type WorkflowDefinitionNode,
  WorkflowNodeType,
  type WorkflowStepProblem,
} from './workflow-definition';

/** The label of the field at a dot path such as `waitMenu.maxAttempts`, if the catalog has it. */
export const fieldLabelAt = (
  fields: WorkflowStepField[],
  path: string,
): string | undefined => {
  const [key, ...rest] = path.replace(/\[\d+\]/g, '').split('.');
  const field = fields.find((candidate) => candidate.key === key);
  if (!field) return undefined;
  return rest.length
    ? (fieldLabelAt(field.fields ?? [], rest.join('.')) ?? field.label)
    : field.label;
};

/** `routingPhoneNumber` → `Routing phone number`. */
const humanize = (name: string) => {
  const words = name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Template text as a person reads it: each `{{input.triggerContext.routingPhoneNumber}}` becomes
 * the name of what it reads, "Routing phone number".
 */
export const readableTemplate = (text: string) =>
  text.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_, path: string) =>
    humanize(path.split('.').filter(Boolean).at(-1) ?? path),
  );

/** The trigger reads as what starts it; unset means a phone call, as the backend defaults it. */
const triggerSentence = (stepType: WorkflowStepType, data: Record<string, unknown>) => {
  const value =
    typeof data.triggerType === 'string'
      ? data.triggerType
      : WorkflowTriggerType.InboundPhoneCall;
  const field = stepType.fields.find(({ key }) => key === 'triggerType');
  return field?.options?.find((option) => option.value === value)?.label ?? humanize(value);
};

/** `input.triggerContext.isContactCenterOpen` → `Is contact center open`: the name of what it reads. */
const readableOperand = (operand: string) =>
  operand.includes('{{')
    ? readableTemplate(operand)
    : humanize(operand.split('.').filter(Boolean).at(-1) ?? operand);

const conditionSentence = (stepType: WorkflowStepType, data: Record<string, unknown>) => {
  const { operand, operator, value } = data;
  if (typeof operand !== 'string' || !operand) return null;

  const operatorField = stepType.fields.find(({ key }) => key === 'operator');
  const operatorLabel =
    operatorField?.options?.find((option) => option.value === operator)?.label ??
    (typeof operator === 'string' ? operator : '');
  const comparedWith = value === undefined || value === null ? '' : ` ${String(value)}`;

  return `${readableOperand(operand)} ${operatorLabel}${comparedWith}`.trim();
};

/** The first field worth showing: text that was typed, not one wired from an earlier step. */
const firstFilledField = (stepType: WorkflowStepType, data: Record<string, unknown>) => {
  for (const field of stepType.fields) {
    const value = data[field.key];
    if (field.reference) continue;

    if (field.kind === WorkflowStepFieldKind.Template && typeof value === 'string' && value) {
      return readableTemplate(value);
    }
    if (field.kind === WorkflowStepFieldKind.KeyValue && value && typeof value === 'object') {
      const names = Object.keys(value);
      if (names.length) return `Sets ${names.join(', ')}`;
    }
  }
  return null;
};

/** What a step's card says before any of its own fields are filled in. */
export const STEP_NOT_SET_UP = 'Not set up yet';

/**
 * A step that needs something filled in, with nothing typed or chosen yet. A field wired from an
 * earlier step doesn't count, and a step whose fields are all optional already runs as it is.
 */
const isNotSetUp = (stepType: WorkflowStepType, data: Record<string, unknown>) =>
  stepType.fields.some(({ required, requiredUnless }) => required || !!requiredUnless) &&
  stepType.fields.every(({ key, reference }) => {
    const value = data[key];
    return !!reference || value === undefined || value === null || value === '';
  });

/**
 * The one line a closed card shows: what the step does, built from its fields — the message, the
 * destination, the condition as `operand operator value`. When the step has a problem, the line
 * says what's wrong instead, so it can be found without opening the card. A step that needs
 * something filled in, with nothing filled in yet, says {@link STEP_NOT_SET_UP}.
 *
 * `stepType` is undefined for a type the catalog doesn't describe.
 */
export function summarizeStep(
  node: WorkflowDefinitionNode,
  stepType: WorkflowStepType | undefined,
  problems: WorkflowStepProblem[] = [],
): string {
  if (!stepType) return `Unknown step type “${node.type}”`;

  const data = node.data ?? {};
  // before the problems: an untouched step's only problem is that it's empty, which this says
  if (!stepType.isTrigger && isNotSetUp(stepType, data)) return STEP_NOT_SET_UP;

  const [problem] = problems;
  if (problem) {
    const label = problem.field ? fieldLabelAt(stepType.fields, problem.field) : undefined;
    return label ? `${label}: ${problem.message}` : problem.message;
  }

  let summary: string | null;
  if (stepType.isTrigger) summary = triggerSentence(stepType, data);
  else if (node.type === WorkflowNodeType.If) summary = conditionSentence(stepType, data);
  else summary = firstFilledField(stepType, data);

  return summary ?? stepType.description;
}
